import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { Navigate } from 'react-router-dom'
import { supabase } from '@/lib/supabaseClient'
import { usePlatformRoleContext } from '@/hooks/usePlatformRoleContext'
import { formatDate } from '@/lib/utils'

interface PersonRow {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string | null
  created_at: string
}

export default function PortalUsers() {
  const { canSeeClients, isLoading: roleLoading, isError: roleError } = usePlatformRoleContext()

  const { data: people, isLoading: peopleLoading, isError: peopleError } = useQuery({
    queryKey: ['people-admin'],
    queryFn: async (): Promise<PersonRow[]> => {
      const { data, error } = await supabase
        .from('people')
        .select('id, first_name, last_name, email, phone, created_at')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as PersonRow[]
    },
    enabled: canSeeClients,
  })

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-white/70" />
      </div>
    )
  }

  if (roleError) {
    return (
      <div className="max-w-md mx-auto py-16 text-center">
        <p className="text-white/80">Unable to verify your access. Check the browser console for details.</p>
      </div>
    )
  }

  if (!canSeeClients) {
    return <Navigate to="/portal" replace />
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">Users</h1>
        <p className="text-white/60 text-sm mt-1">All users and contacts in the platform.</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="rounded-xl border border-white/15 bg-white/5 overflow-hidden backdrop-blur-sm"
      >
        {peopleLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" />
          </div>
        ) : peopleError ? (
          <div className="px-4 py-8 text-center">
            <p className="text-white/80">Unable to load users.</p>
            <p className="text-white/50 text-sm mt-1">Check the browser console for details. You may need to run migrations to grant SELECT on the people table.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/15">
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Name</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Email</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Phone</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Created</th>
                </tr>
              </thead>
              <tbody>
                {(people ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-white/50 text-sm">
                      No users yet.
                    </td>
                  </tr>
                ) : (
                  (people ?? []).map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-white/10 last:border-0 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-white">
                        {[p.first_name, p.last_name].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={`mailto:${p.email}`}
                          className="text-white/90 hover:text-white hover:underline"
                        >
                          {p.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-white/80">{p.phone ?? '—'}</td>
                      <td className="px-4 py-3 text-white/70 text-sm">{formatDate(p.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  )
}
